import { IComponentSetDto, KAgentHelper } from "@kuika/kuika-cl-model";
import * as React from "react";
import { ComponentSetService } from "../../../../services/component-set-service";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { IWithHistoryProp, withHistory } from "../../../../shared/hoc/with-history";
import { withManagement } from "../../../../shared/hoc/with-management";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaBadge from "../../../ui-kit/kuika-badge/kuika-badge";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaListPage, {
  KuikaListPageHeader,
  KuikaListPageToolbar
} from "../../../ui-kit/kuika-list-page/kuika-list-page";
import KuikaListTable, {
  KuikaListTableColumn,
  KuikaListTableRowAction
} from "../../../ui-kit/kuika-list-table/kuika-list-table";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import "../settings-panel.scss";

export interface IComponentSetState {
  componentSetList: IComponentSetDto[];
  componentSetToDelete: IComponentSetDto;
  componentSetToEdit: IComponentSetDto;
  isNewComponentSetModalActive: boolean;
  triggerComponentSetDeleteModal: boolean;
  searchKeyWords: string[];
  searchText: string;
  isDataLoading: boolean;
  isPublicCsManageDomainsModalActive: boolean;
  componentSetToManageDomains: IComponentSetDto;
}

export interface IComponentSetProps {}

class ComponentSet extends React.PureComponent<
  IComponentSetProps & IWithContextProp & IWithHistoryProp,
  IComponentSetState
> {
  constructor(props: IComponentSetProps & IWithContextProp & IWithHistoryProp) {
    super(props);
    this.state = {
      componentSetList: [],
      componentSetToDelete: {} as IComponentSetDto,
      componentSetToEdit: {} as IComponentSetDto,
      isNewComponentSetModalActive: false,
      triggerComponentSetDeleteModal: false,
      searchKeyWords: [],
      searchText: "",
      isDataLoading: true,
      isPublicCsManageDomainsModalActive: false,
      componentSetToManageDomains: {} as IComponentSetDto
    };
  }

  async loadComponentSetList() {
    this.setState({ isDataLoading: true });
    try {
      const res = await ComponentSetService.Get("4bd34537-525c-4a7c-dde0-08d911312c71");
      if (res && res.status === 200) {
        const list = [] as IComponentSetDto[];
        list.push(res.data);
        this.setState({ componentSetList: list });
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
    this.setState({ isDataLoading: false });
  }

  async componentDidMount() {
    await this.loadComponentSetList();
  }

  async editComponentSet(e: React.MouseEvent | undefined, componentSet: IComponentSetDto) {
    e?.stopPropagation();
    this.setState({ componentSetToEdit: componentSet, isNewComponentSetModalActive: true });
  }

  triggerDeleteComponentSet(e: React.MouseEvent | undefined, componentSet: IComponentSetDto) {
    e?.stopPropagation();
    this.setState({
      triggerComponentSetDeleteModal: true,
      componentSetToDelete: componentSet
    });
  }

  setComponentSetAsPublic = async (e: React.MouseEvent | undefined, componentSet: IComponentSetDto) => {
    e?.stopPropagation();
    // const res = await ComponentSetService.SetAsPublic(componentSet.id);
    // if (res && res.status === 200) {
    //   this.loadComponentSetList();
    // }
  };

  openPublicCsManageDomainsModal = async (e: React.MouseEvent | undefined, componentSet: IComponentSetDto) => {
    e?.stopPropagation();
    this.setState({ isPublicCsManageDomainsModalActive: true, componentSetToManageDomains: componentSet });
  };

  openComponentSetAddModal = async () => {
    this.setState({ componentSetToEdit: {} as IComponentSetDto, isNewComponentSetModalActive: true });
  };

  closeComponentSetAddModal = async (isReloadRequired: boolean) => {
    if (isReloadRequired === true) {
      this.setState({ isNewComponentSetModalActive: false }, this.loadComponentSetList);
    } else {
      this.setState({ isNewComponentSetModalActive: false });
    }
  };

  getComponentSetColumns = (): KuikaListTableColumn<IComponentSetDto>[] => {
    return [
      {
        id: "name",
        header: () => "Name",
        headerTestId: "componentSetNameHeader",
        accessorFn: (row: IComponentSetDto) => row.name?.toLowerCase(),
        cell: (row: IComponentSetDto) => (
          <span className="kuika-component-set-list__name">
            <KuikaSvg width={16} height={16} type="sidebar-icons" name="notebook" color="var(--dark-6)" />
            <span className="kuika-component-set-list__name-text">{row.name}</span>
            {row.isPublic && (
              <KuikaTooltip content="Public component set, managed by Kuika.">
                <span className="kuika-component-set-list__badge">
                  <KuikaBadge content="Public" color="yellow" size="small" />
                </span>
              </KuikaTooltip>
            )}
          </span>
        )
      }
    ];
  };

  getRowActions = (): KuikaListTableRowAction<IComponentSetDto>[] => {
    const isKuikaDeveloper: boolean = KMainFunctions.isUserKuikaDeveloper(this.props.context?.userInfo?.username);
    return [
      {
        key: "edit",
        label: "Edit",
        dataTestId: "componentSetEditAction",
        icon: <KuikaSvg name="edit" width={14} height={14} />,
        onSelect: (row: IComponentSetDto) => this.editComponentSet(undefined, row)
      },
      {
        key: "setAsPublic",
        label: "Set as public",
        dataTestId: "componentSetSetPublicAction",
        icon: <KuikaSvg name="world-www" width={14} height={14} />,
        hidden: (row: IComponentSetDto) => row.isPublic === true || !isKuikaDeveloper,
        onSelect: (row: IComponentSetDto) => this.setComponentSetAsPublic(undefined, row)
      },
      {
        key: "manageDomains",
        label: "Manage domains",
        dataTestId: "componentSetManageDomainsAction",
        icon: <KuikaSvg name="world-www" width={14} height={14} />,
        hidden: (row: IComponentSetDto) => row.isPublic !== true || !isKuikaDeveloper,
        onSelect: (row: IComponentSetDto) => this.openPublicCsManageDomainsModal(undefined, row)
      },
      {
        key: "delete",
        label: "Delete",
        danger: true,
        dataTestId: "componentSetDeleteAction",
        icon: <KuikaSvg name="trash" width={14} height={14} />,
        onSelect: (row: IComponentSetDto) => this.triggerDeleteComponentSet(undefined, row)
      }
    ];
  };

  handleSearchInputChange = (e: any) => {
    this.setState({
      searchKeyWords: e.target.value.split(" "),
      searchText: e.target.value
    });
  };

  closePublicCompSetManageDomainsModalActive = (isDataChanged: boolean) => {
    this.setState({ isPublicCsManageDomainsModalActive: false });
    if (isDataChanged) {
      this.loadComponentSetList();
    }
  };

  deleteSearchText = () => this.setState({ searchText: "" });

  render() {
    const filteredComponentSet: IComponentSetDto[] = KAgentHelper.filterArray(
      this.state.componentSetList,
      this.state.searchKeyWords
    );
    return (
      <KuikaListPage
        className="kuika-component-set-list"
        data-testid="componentSetListPage"
        busy={this.state.isDataLoading}
      >
        <KuikaListPageHeader
          icon={<KuikaSvg width={20} height={20} type="sidebar-icons" name="notebook" />}
          title="Component Sets"
          titleTestId="componentSetListTitle"
        />

        <KuikaListPageToolbar
          search={
            <KuikaInput
              data-testid="componentSetSearchInput"
              placeholder="Search component sets"
              value={this.state.searchText}
              leftIcon={<KuikaSvg name="search" width={16} height={16} color="var(--dark-6)" />}
              onChange={(e) => this.handleSearchInputChange(e)}
            />
          }
        />

        <KuikaListTable<IComponentSetDto>
          data-testid="componentSetTable"
          data={filteredComponentSet}
          columns={this.getComponentSetColumns()}
          rowKey={(row: IComponentSetDto) => row.id}
          rowActions={this.getRowActions()}
          rowActionsTestId={(row: IComponentSetDto) => `componentSetRowActions-${row.name}`}
          loading={this.state.isDataLoading}
          onRowClick={(row: IComponentSetDto) => this.props.navigator.gotoComponentList(row.id)}
          emptyTitle={this.state.searchText ? "No component sets match your search" : "No component sets yet"}
          emptyDescription={
            this.state.searchText
              ? "Try a different name."
              : "A component set holds the components your team can design with."
          }
        />
      </KuikaListPage>
    );
  }
}

const componentSet = withContext<IComponentSetProps>(
  withHistory<IComponentSetProps>(withManagement<IComponentSetProps>(ComponentSet))
);
export { componentSet as ComponentSet };
